/**
 * layoutResponse — turn the flat block list the markdown parser produces into
 * the five-part anatomy of the AI Response Pattern:
 *
 *   headline · caveats · signals · lead · sections · actions
 *
 * Pure function, no React — unit-testable. Block shapes (from
 * AIChat.renderMarkdown):
 *   { kind:'p', text, el }                 paragraph (el = inline-formatted node)
 *   { kind:'heading', level, text, el }
 *   { kind:'caveat', text, el, tone }      "> ⚠ …" blockquote line
 *   { kind:'signals', signals:[…] }        ```signals fence
 *   { kind:'list', ordered, items:[{text, el}] }
 *   { kind:'table', rows, el }             markdown table (el = rendered)
 *   { kind:'chart'|'pnl'|'briefing'|'fa'|'table-loading'|'chart-loading'|…, el }
 *   { kind:'forecast'|'task_approval'|'email_preview'|'ask', el }   action cards
 */

export const ACTION_KINDS = new Set(['forecast', 'task_approval', 'email_preview', 'ask']);
export const DATA_VIEW_KINDS = new Set(['chart', 'table', 'pnl', 'briefing', 'fa', 'chart-loading', 'table-loading', 'briefing-loading', 'pnl-loading', 'forecast-loading']);
const RECOMMEND_LABEL = /recommend|action|adjust|next step|what to do|fix|suggest|to do|priorit/i;
const SOURCES_LABEL = /^(?:sources?|assumptions?|sources?\s*(?:&|and)\s*assumptions?|method(?:ology)?|data\s+notes?|basis|provenance)\b/i;
const HEADLINE_MAX = 260;

/** First sentence of a paragraph, capped for the collapsed-section preview. */
export function previewOf(text, max = 140) {
  const t = String(text || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  // Split on the first sentence end that leaves something meaningful behind.
  const m = t.match(/^(.{24,}?[.!?])(?:\s|$)/);
  let out = m ? m[1] : t;
  if (out.length > max) out = out.slice(0, max - 1).replace(/\s+\S*$/, '') + '…';
  return out;
}

/** "Recommended adjustments (3)" → { label, count } */
function splitLabelCount(text) {
  const t = String(text || '').trim();
  const m = t.match(/^(.*?)\s*[(（]\s*(\d{1,3})\s*[)）]\s*$/);
  if (m) return { label: m[1].trim(), count: Number(m[2]) };
  const m2 = t.match(/^(.*?)\s*[·—–-]\s*(\d{1,3})\s*$/);
  if (m2) return { label: m2[1].trim(), count: Number(m2[2]) };
  return { label: t, count: null };
}

/** Parse "**Title** — body" / "**Title**: body" / "Title — body" / "Title. body" */
export function splitRecommendation(text) {
  const raw = String(text || '').trim();
  let m = raw.match(/^\*\*(.+?)\*\*\s*(?:[—–:-]\s*)?([\s\S]*)$/);
  if (m) return { title: m[1].trim().replace(/[.:]$/, ''), body: m[2].trim() };
  m = raw.match(/^(.{6,80}?)\s+[—–]\s+([\s\S]+)$/);
  if (m) return { title: m[1].trim(), body: m[2].trim() };
  m = raw.match(/^(.{6,80}?[.:])\s+([\s\S]{20,})$/);
  if (m) return { title: m[1].trim().replace(/[.:]$/, ''), body: m[2].trim() };
  return { title: raw.replace(/\*\*/g, ''), body: '' };
}

function isSourcesLabel(label) { return SOURCES_LABEL.test(String(label || '').trim()); }
function isRecommendLabel(label) { return RECOMMEND_LABEL.test(String(label || '')); }

function describeSection(sec) {
  const blocks = sec.blocks;
  const firstP = blocks.find(b => b.kind === 'p');
  const list = blocks.find(b => b.kind === 'list');
  const table = blocks.find(b => b.kind === 'table');
  const hasChart = blocks.some(b => b.kind === 'chart' || b.kind === 'chart-loading');
  const hasPnl = blocks.some(b => b.kind === 'pnl');
  const hasBriefing = blocks.some(b => b.kind === 'briefing');

  let preview = firstP ? previewOf(firstP.text) : '';
  if (!preview && list) {
    const titles = list.items.map(it => splitRecommendation(it.text).title).filter(Boolean);
    preview = titles.join(' · ');
  }
  if (!preview && table) {
    const n = Math.max(0, (table.rows || []).filter(r => !/^\s*\|?\s*:?-{2,}/.test(r)).length - 1);
    preview = n ? `${n} row${n === 1 ? '' : 's'}` : '';
  }

  let count = sec.count;
  let countTone = null;
  if (count == null && list && list.ordered && list.items.length > 1) count = list.items.length;
  if (count != null && (isRecommendLabel(sec.label) || (list && list.ordered))) countTone = 'accent';

  const hints = [];
  if (hasChart) hints.push('chart · table');
  else if (table) hints.push('table');
  if (hasPnl) hints.push('P&L');
  if (hasBriefing) hints.push('cards');

  return {
    ...sec,
    preview,
    count,
    countTone,
    hint: hints.length ? hints[0] : null,
    isSources: isSourcesLabel(sec.label),
    isRecommend: isRecommendLabel(sec.label) && !!list && list.ordered,
    hasDataView: blocks.some(b => DATA_VIEW_KINDS.has(b.kind)),
  };
}

/**
 * @param {Array} blocks
 * @returns {{headline, caveats, signals, lead, sections, actions, structured}}
 */
export function layoutResponse(blocks) {
  const list = Array.isArray(blocks) ? blocks.filter(Boolean) : [];
  const actions = [];
  const body = [];
  for (const b of list) {
    if (ACTION_KINDS.has(b.kind)) actions.push(b);
    else body.push(b);
  }

  // Sections: every heading (any level) that appears after the first heading
  // opens a new section; blocks before the first heading are the lead. A
  // deeper heading inside a section stays inside it as a sub-heading.
  const pre = [];
  const sections = [];
  let current = null;
  let sectionLevel = null;
  for (const b of body) {
    if (b.kind === 'heading') {
      if (current == null || b.level <= sectionLevel) {
        const { label, count } = splitLabelCount(b.text);
        current = { label, count, level: b.level, blocks: [] };
        sectionLevel = b.level;
        sections.push(current);
        continue;
      }
      // deeper heading within the open section → keep as content
      current.blocks.push(b);
      continue;
    }
    if (current) current.blocks.push(b);
    else pre.push(b);
  }

  // Hoist caveats + the first signals strip out of the lead.
  const caveats = [];
  let signals = null;
  const lead = [];
  for (const b of pre) {
    if (b.kind === 'caveat') { caveats.push(b); continue; }
    if (b.kind === 'signals' && !signals) { signals = b; continue; }
    lead.push(b);
  }
  // Signals emitted inside the first section (models do that) still belong
  // on the strip. Only the first strip is used; duplicates are dropped.
  for (const sec of sections) {
    const idx = sec.blocks.findIndex(b => b.kind === 'signals');
    if (idx >= 0) {
      if (!signals) signals = sec.blocks[idx];
      sec.blocks.splice(idx, 1);
    }
  }
  // Caveats that sit at the very top of the first section (before any prose)
  // are answer-level caveats too.
  if (sections.length && lead.length === 0) {
    const first = sections[0];
    while (first.blocks.length && first.blocks[0].kind === 'caveat') caveats.push(first.blocks.shift());
  }

  const structured = sections.length > 0 || !!signals || actions.length > 0
    || lead.some(b => DATA_VIEW_KINDS.has(b.kind));

  // Headline: the first paragraph of a structured answer, when it is one
  // sentence-ish. Anything longer stays ordinary prose.
  let headline = null;
  if (structured && lead.length && lead[0].kind === 'p') {
    const text = String(lead[0].text || '').trim();
    if (text.length <= HEADLINE_MAX && !/^[-*•]\s/.test(text)) {
      headline = lead.shift();
    }
  }

  return {
    headline,
    caveats,
    signals: signals ? (signals.signals || []) : null,
    lead,
    sections: sections.map(describeSection),
    actions,
    structured,
  };
}

/** Coerce whatever the model put in a ```signals fence into [{label,value,sub,tone}] */
export function normalizeSignals(payload) {
  let arr = payload;
  if (payload && !Array.isArray(payload)) {
    arr = payload.signals || payload.items || payload.data || payload.figures || [];
  }
  if (!Array.isArray(arr)) return [];
  const TONES = new Set(['accent', 'positive', 'negative', 'muted']);
  return arr
    .map(s => {
      if (!s || typeof s !== 'object') return null;
      const label = String(s.label ?? s.name ?? s.title ?? '').trim();
      const value = s.value ?? s.figure ?? s.amount;
      if (!label || value == null || String(value).trim() === '') return null;
      let tone = String(s.tone || s.emphasis || '').toLowerCase();
      if (tone === 'highlight' || tone === 'primary') tone = 'accent';
      if (tone === 'good' || tone === 'up') tone = 'positive';
      if (tone === 'bad' || tone === 'down') tone = 'negative';
      return {
        label,
        value: typeof value === 'number' ? value.toLocaleString('en-US') : String(value),
        sub: s.sub != null ? String(s.sub) : (s.subtitle != null ? String(s.subtitle) : (s.context != null ? String(s.context) : '')),
        tone: TONES.has(tone) ? tone : null,
      };
    })
    .filter(Boolean)
    .slice(0, 5);
}
